import { Alert, Avatar, Box, Button, Chip, Grid, IconButton, Skeleton, Snackbar, Stack, Tooltip, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PlaceIcon from "@mui/icons-material/Place";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ShareIcon from "@mui/icons-material/Share";
import { Link as RouterLink, useParams } from "react-router";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useContentDetail, useContentList } from "../hooks/useApiQueries.js";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed.js";
import { ContentCard } from "../components/ContentCard.js";
import { SEO } from "../components/SEO.js";
import { NeonEyebrow, NeonDivider, neonButtonSx } from "../components/Neon.js";
import { proxied, isLikelyImageUrl } from "../lib/imageProxy.js";
import {
  decodeHtml,
  displaySource,
  formatDuration,
  frontendDisplayReason,
  looksLikeDomain,
} from "../lib/displayHelpers.js";

function deriveYouTubeEmbed(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export function ContentDetail() {
  const { slug = "" } = useParams();
  const { data, isLoading, error } = useContentDetail(slug);
  const [shareFeedback, setShareFeedback] = useState(false);
  const { track: trackRecent } = useRecentlyViewed();

  // Push to localStorage as soon as the data arrives so the Home "Recently
  // viewed" strip surfaces it next visit. Only fires once per data identity
  // (slug change), not on every re-render.
  useEffect(() => {
    if (!data) return;
    trackRecent({
      id: data.id,
      slug: data.slug,
      title: data.title,
      type: data.type,
      thumbnailUrl: data.thumbnailUrl,
      sourceAuthor: data.sourceAuthor,
      publishedAt: data.publishedAt,
    });
  }, [data, trackRecent]);

  // Pre-fetch related content by the first confirmed equipment slug.
  const equipmentSlug = data?.equipmentTags.find((t) => t.tractrsId && t.confirmed)?.slug;
  const { data: related } = useContentList({
    equipmentSlug: equipmentSlug ?? undefined,
    pageSize: 8,
  });
  const relatedItems = related?.data.filter((c) => c.slug !== slug).slice(0, 4) ?? [];

  if (isLoading) return <Skeleton variant="rectangular" height={500} />;
  if (error || !data) return <Alert severity="error">Content not found.</Alert>;

  const isVideo = data.type === "VIDEO";
  const embedUrl = data.embedUrl ?? (isVideo ? deriveYouTubeEmbed(data.sourceUrl) : null);
  const title = decodeHtml(data.title);
  const description = decodeHtml(data.description);
  const sourceLabel = data.sourceAuthor ?? null;
  const sourceDisplay = displaySource(sourceLabel);
  const sourceIsDomain = looksLikeDomain(sourceLabel);
  const cleanReason = frontendDisplayReason(null); // (no aiReason on detail endpoint)
  const duration = formatDuration(data.durationSec);
  const dateLabel = data.publishedAt ? dayjs(data.publishedAt).format("D MMM YYYY") : null;

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title, url }).catch(() => null);
    } else {
      await navigator.clipboard.writeText(url).catch(() => null);
      setShareFeedback(true);
    }
  };

  const jsonLd =
    data.type === "VIDEO"
      ? [
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: title,
            description: description || "",
            thumbnailUrl: data.thumbnailUrl ?? undefined,
            uploadDate: data.publishedAt ?? undefined,
            embedUrl: embedUrl ?? undefined,
          },
        ]
      : undefined;

  return (
    <>
      <SEO
        title={title}
        description={description || `${title} on Corian Park`}
        canonical={`https://corianpark.au/content/${data.slug}`}
        ogImage={data.thumbnailUrl ?? undefined}
        jsonLd={jsonLd}
        type="article"
        article={{
          publishedTime: data.publishedAt ?? undefined,
          author: data.sourceAuthor ?? "Corian Park",
        }}
      />

      {/* Header band — neon eyebrow + chips meta */}
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
        <NeonEyebrow>{isVideo ? "VIDEO" : "ARTICLE"}</NeonEyebrow>
        {dateLabel && (
          <Chip
            size="small"
            icon={<CalendarMonthIcon sx={{ fontSize: 14 }} />}
            label={dateLabel}
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
        {duration && (
          <Chip
            size="small"
            icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
            label={duration}
            variant="outlined"
          />
        )}
        {data.featured && <Chip size="small" label="Featured" color="primary" variant="outlined" />}
        {data.tags.includes("new-release") && (
          <Chip size="small" label="New release" color="primary" />
        )}
      </Stack>

      <Stack direction="row" sx={{ mb: 2, alignItems: "flex-start", justifyContent: "space-between" }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 32, md: 44 },
            fontWeight: 900,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            flex: 1,
          }}
        >
          {title}
        </Typography>
        <Tooltip title="Share">
          <IconButton onClick={handleShare} sx={{ ml: 1, mt: 0.5, color: "primary.main", flexShrink: 0 }}>
            <ShareIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Company attribution takes priority over the plain text byline when the
          item was submitted by a promoting company — links to their public
          profile and shows their logo. */}
      {data.submittedByCompany ? (
        <Stack
          direction="row"
          spacing={1.25}
          component={RouterLink}
          to={`/company/${data.submittedByCompany.slug}`}
          sx={{
            alignItems: "center",
            mb: 3,
            textDecoration: "none",
            color: "inherit",
            width: "fit-content",
            "&:hover .company-name": { color: "primary.main" },
          }}
        >
          <Avatar
            src={data.submittedByCompany.logoUrl ?? undefined}
            sx={{
              width: 36,
              height: 36,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              fontSize: 16,
              fontWeight: 800,
            }}
          >
            {data.submittedByCompany.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1 }}>
              Submitted by
            </Typography>
            <Typography
              className="company-name"
              variant="subtitle2"
              sx={{ fontWeight: 800, lineHeight: 1.2, transition: "color 120ms ease" }}
            >
              {data.submittedByCompany.name}
            </Typography>
          </Box>
        </Stack>
      ) : (
        sourceDisplay && (
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            {sourceIsDomain ? "Reported by " : "By "}
            <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
              {sourceDisplay}
            </Box>
          </Typography>
        )
      )}

      {/* Hero media */}
      {embedUrl ? (
        <Box sx={{ position: "relative", width: "100%", aspectRatio: "16 / 9", mb: 4, borderRadius: 1, overflow: "hidden" }}>
          <Box
            component="iframe"
            src={embedUrl}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          />
        </Box>
      ) : isLikelyImageUrl(data.thumbnailUrl) ? (
        <Box sx={{ position: "relative", mb: 4, borderRadius: 1, overflow: "hidden" }}>
          <Box
            component="img"
            src={proxied(data.thumbnailUrl!)}
            alt={title}
            loading="eager"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
            sx={{
              width: "100%",
              aspectRatio: "16 / 9",
              objectFit: "cover",
              display: "block",
              boxShadow: "0 0 40px -10px rgba(255,176,0,0.3)",
            }}
          />
          {/* neon stripe under hero */}
          <Box
            sx={{
              height: 3,
              background: "linear-gradient(90deg, #ffb000 0%, #ff8800 50%, transparent 100%)",
              boxShadow: "0 0 10px rgba(255,176,0,0.6)",
            }}
          />
        </Box>
      ) : null}

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 8 }}>
          {description && (
            <Typography
              variant="body1"
              sx={{ fontSize: 17, lineHeight: 1.75, mb: 3, whiteSpace: "pre-wrap" }}
            >
              {description}
            </Typography>
          )}
          {cleanReason && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              <NeonEyebrow sx={{ display: "block", mb: 0.5 }}>EDITORIAL NOTE</NeonEyebrow>
              {cleanReason}
            </Typography>
          )}

          {data.sourceUrl && (
            <Button
              component="a"
              href={data.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              sx={{
                fontWeight: 700,
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": { boxShadow: "0 0 18px rgba(255,176,0,0.45)" },
              }}
            >
              Read the full {isVideo ? "story" : "article"} {sourceDisplay ? `on ${sourceDisplay}` : ""}
            </Button>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              p: 3,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              background:
                "linear-gradient(180deg, rgba(255,176,0,0.05) 0%, transparent 100%)",
            }}
          >
            {data.equipmentTags.length > 0 && (
              <>
                <NeonEyebrow>{isVideo ? "EQUIPMENT IN THIS VIDEO" : "EQUIPMENT MENTIONED"}</NeonEyebrow>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, mb: 2, gap: 1, flexWrap: "wrap" }}>
                  {data.equipmentTags.map((t, i) => {
                    const label = `${t.make ?? ""} ${t.model ?? ""}`.trim() || t.slug;
                    if (!label) return null;
                    const linkable = !!t.tractrsId && t.confirmed;
                    return linkable ? (
                      <Chip
                        key={i}
                        component={RouterLink}
                        to={`/equipment/${t.slug}`}
                        label={label}
                        clickable
                        sx={{
                          bgcolor: "primary.main",
                          color: "common.black",
                          fontWeight: 700,
                          "&:hover": { boxShadow: "0 0 14px rgba(255,176,0,0.55)" },
                        }}
                      />
                    ) : (
                      <Chip key={i} label={label} variant="outlined" />
                    );
                  })}
                </Stack>
                <NeonDivider width={48} sx={{ my: 2 }} />
              </>
            )}

            {data.postcode && (
              <>
                <NeonEyebrow>FILMED NEAR</NeonEyebrow>
                <Box sx={{ mt: 1.5, mb: 2 }}>
                  <Chip
                    component={RouterLink}
                    to={`/built-near-you/${data.postcode}`}
                    icon={<PlaceIcon sx={{ fontSize: 16 }} />}
                    label={`${data.region ?? "Postcode"} · ${data.postcode}`}
                    clickable
                    sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
                  />
                </Box>
                <NeonDivider width={48} sx={{ my: 2 }} />
              </>
            )}

            <Button
              component={RouterLink}
              to="/quote"
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<RequestQuoteIcon />}
              sx={{ ...neonButtonSx, mt: 1 }}
            >
              Get a quote
            </Button>
          </Box>
        </Grid>
      </Grid>

      {relatedItems.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <NeonDivider sx={{ mb: 3 }} />
          <NeonEyebrow>MORE LIKE THIS</NeonEyebrow>
          <Typography
            variant="h3"
            sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, mt: 1, mb: 3 }}
          >
            You might also like
          </Typography>
          <Grid container spacing={2}>
            {relatedItems.map((item) => (
              <Grid key={item.id} size={{ xs: 12, sm: 6, md: 3 }}>
                <ContentCard item={item} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <Snackbar
        open={shareFeedback}
        autoHideDuration={2500}
        onClose={() => setShareFeedback(false)}
        message="Link copied!"
      />
    </>
  );
}
